import {
  Component,
  computed,
  DestroyRef,
  ErrorHandler,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { AnalyticsService } from '../../../../../analytics';
import type { ChartFilenameStem } from '../../../../../charts/chart.filename';
import { ERROR_FEEDBACK_MS } from './action-feedback';

/**
 * What the menu can save. `'png'` lives here and never in
 * `SdTableExportFormat`, so `tableFilename(…, 'png')` and a
 * `table_downloaded` with `format: 'png'` do not type-check.
 */
type SaveFormat = fromSeriousSystem.SdTableExportFormat | 'png';

/**
 * The trigger's name. A literal union, never a free string: the pipe would
 * look model text up as a catalog path.
 */
export type ResponseTableSaveLabelKey =
  'CHAT.TABLE.SAVE' | 'CHAT.CHART.DOWNLOAD';

/**
 * The Table widget's save action. Decisions cited below as `KD-n` resolve in
 * SB-3514's Technical Plan,
 * `.ai/plans/SB-3514_table-rendering-in-chat/SB-3514-technical-plan.md`.
 *
 * Projected into `<sd-response-table>`'s title-bar slot, beside the copy action,
 * for the same two reasons: the widget is a `serious-system` primitive that
 * cannot depend on the app, and this button reports to app-level analytics. Only
 * the pure serializers live in the library, in `response-table.export.ts`, where
 * they are testable without a DOM.
 *
 * Both formats carry the **whole** model — every row and column, past both
 * render caps — because the caps are applied at the widget's render loop and
 * never to the model itself (KD-11).
 *
 * **Mobile is `sd-dropdown-menu`'s own behavior, not a second implementation.**
 * `shouldRenderDrawer()` swaps the CDK overlay for a bottom drawer, and Escape,
 * outside tap and selection all dismiss on both paths because both paths are the
 * dropdown's. Nothing here branches on viewport.
 *
 * **the one Download menu for the table and the chart.** With
 * its defaults it is the table's save action, byte for byte. The chart card
 * sets `labelKey`, `filenameStem` (every file then downloads as
 * `${stem}.${format}`) and `png`, which adds "Download as PNG" first. The menu
 * opens under the button with its right edge on the button's right edge
 * (`SD_MENU_ALIGN_END`), for the table too.
 */
@Component({
  selector: 'squadbox-response-table-save',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    fromSeriousSystem.SpinnerDirective,
    TranslateModule,
    MatTooltipModule,
  ],
  template: `
    <button
      type="button"
      sdIconButton
      variant="icon"
      size="xs"
      [color]="hasError() ? 'warning' : 'neutral'"
      [attr.aria-label]="labelKey() | translate"
      [sdDropdownMenuTrigger]="saveDropdown"
      [cdkMenuPosition]="alignEnd"
      [disabled]="isSaving()"
      [matTooltip]="tooltipKey() | translate"
      matTooltipPosition="below"
    >
      @if (isSaving()) {
        <div sdSpinner size="sm" color="primary"></div>
      } @else {
        <!-- The download glyph, not the save one: Figma's Table Header draws an
             arrow into a tray, while ui-basics' save is a floppy disk. Pick
             this glyph against the design, not by name. -->
        <svg sdUseUIBasicIcon="download"></svg>
      }
    </button>

    <!-- The failure has to reach a screen reader, and neither of the other two
         signals does. The warning color is color alone, and the tooltip only
         opens on hover or focus of the element that carries it. The aria-label
         stays "Save table" on purpose — it is the control's name, not its
         state — so without this region a blind user gets nothing at all.
         Rendered only while the error is live, so the region is empty at rest
         and the text is a change for the live announcer to pick up. -->
    <span class="sr-only" role="status">
      @if (hasError()) {
        {{ 'CHAT.TABLE.DOWNLOAD_ERROR' | translate }}
      }
    </span>

    <sd-dropdown-menu
      #saveDropdown
      [options]="options()"
      [ariaLabel]="labelKey() | translate"
    ></sd-dropdown-menu>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ResponseTableSaveComponent {
  private readonly translate = inject(TranslateService);

  /** `app.config.ts` provides `Sentry.createErrorHandler()` for this token. */
  private readonly errorHandler = inject(ErrorHandler);

  public readonly table = input.required<fromSeriousSystem.SdResponseTable>();

  /** The trigger's and the menu's name: "Save table", or "Download" on a chart. */
  public readonly labelKey =
    input<ResponseTableSaveLabelKey>('CHAT.TABLE.SAVE');

  /**
   * A chart's download stem. `null` (a real table) keeps `tableFilename`.
   * Branded: only `chartFilenameStem` makes one, so a raw title cannot reach
   * `a.download`.
   */
  public readonly filenameStem = input<ChartFilenameStem | null>(null);

  /**
   * Composes the chart's PNG. `null` means no PNG option. The input holds the
   * function, so it is read with `this.png()` and then called.
   */
  public readonly png = input<(() => Promise<Blob>) | null>(null);

  /**
   * The PNG option's composer and file stem, offered only when both are set:
   * a chart PNG is always named after the chart, never by a fallback here.
   */
  private readonly pngSave = computed(
    (): {
      readonly compose: () => Promise<Blob>;
      readonly stem: ChartFilenameStem;
    } | null => {
      const compose = this.png();
      const stem = this.filenameStem();
      return compose === null || stem === null ? null : { compose, stem };
    },
  );

  /** Right edges aligned, flipping above when there is no room below. */
  protected readonly alignEnd = fromSeriousSystem.SD_MENU_ALIGN_END;

  /**
   * `table_downloaded` fires from here, not from an `@Output` a host wires —
   * see the copy action's note on why. It fires only once a download has
   * actually started, after the blob exists and never on the failure path, so
   * the count is downloads rather than intents.
   */
  private readonly analyticsService = inject(AnalyticsService);

  protected readonly isSaving = signal(false);
  protected readonly hasError = signal(false);

  /**
   * The translation *key*, resolved by the pipe in the template rather than by
   * `TranslateService.instant()` — the same rule the copy action follows.
   */
  protected readonly tooltipKey = computed(() =>
    this.hasError() ? 'CHAT.TABLE.DOWNLOAD_ERROR' : this.labelKey(),
  );

  /**
   * A language ticker, read by `options` purely so the computed re-runs.
   *
   * `DropdownMenuOption.label` is a plain `string`, so there is no pipe to defer
   * to and `instant()` is unavoidable — but `instant()` is **not** a signal read,
   * so without this the labels would memoize the boot-time language and a
   * mid-session switch would leave the menu in French forever. `instant()`
   * inside a `computed()` has this trap wherever it appears.
   */
  private readonly language = toSignal(this.translate.onLangChange, {
    initialValue: null,
  });

  protected readonly options = computed<fromSeriousSystem.DropdownMenuOption[]>(
    () => {
      this.language();
      // No `icon` on either option: Figma's dropdown (11833:4415) is text-only.
      // fileType glyphs here were never in the design.
      const png: fromSeriousSystem.DropdownMenuOption[] =
        this.pngSave() === null
          ? []
          : [
              {
                label: this.translate.instant(
                  'CHAT.CHART.DOWNLOAD_PNG',
                ) as string,
                action: () => void this.save('png'),
              },
            ];
      return [
        ...png,
        {
          label: this.translate.instant('CHAT.TABLE.DOWNLOAD_CSV') as string,
          action: () => void this.save('csv'),
        },
        {
          label: this.translate.instant('CHAT.TABLE.DOWNLOAD_XLSX') as string,
          action: () => void this.save('xlsx'),
        },
      ];
    },
  );

  /**
   * The error state's reset timer, held so it can be cancelled.
   *
   * Two things need it: a component destroyed inside the window would otherwise
   * write to a signal after teardown, and a second failure inside the window
   * would stack a timer that clears the state early.
   */
  private errorResetTimer: ReturnType<typeof setTimeout> | undefined;

  /**
   * Set on destroy. A save that finishes after that was abandoned — the chart
   * switched to its table, or the chat closed, while its PNG was composing —
   * so it downloads nothing, reports nothing and schedules nothing.
   */
  private destroyed = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      clearTimeout(this.errorResetTimer);
    });
  }

  private async save(format: SaveFormat): Promise<void> {
    if (this.isSaving()) {
      return;
    }
    this.isSaving.set(true);
    this.hasError.set(false);

    const table = this.table();
    const stem = this.filenameStem();

    try {
      if (format === 'png') {
        // No analytics for a PNG (P6). A failure takes the same error path
        // as the .csv and the .xlsx.
        // Read at click time: the inputs may have changed since the menu
        // opened.
        const target = this.pngSave();
        if (target === null) {
          return;
        }
        const blob = await target.compose();
        if (this.destroyed) {
          return;
        }
        fromSeriousSystem.triggerDownload(blob, `${target.stem}.png`);
        return;
      }

      const blob =
        format === 'csv'
          ? new Blob([fromSeriousSystem.serializeCsv(table)], {
              type: 'text/csv;charset=utf-8',
            })
          : await fromSeriousSystem.buildXlsx(table);

      fromSeriousSystem.triggerDownload(
        blob,
        stem === null
          ? fromSeriousSystem.tableFilename(table.title, format)
          : `${stem}.${format}`,
      );

      // The counts come from `tableActionMetrics` rather than being spread on
      // here, which is what keeps this payload exactly `table_copied`'s plus
      // `format` instead of a superset only this component knows about.
      this.analyticsService
        .sendTableDownloadedEvent({
          ...fromSeriousSystem.tableActionMetrics(table),
          format,
        })
        .subscribe();
    } catch (error) {
      if (this.destroyed) {
        return;
      }
      // `buildXlsx` fetches its writer chunk on first use, so an offline user
      // reaches here. Reported rather than swallowed — a download that silently
      // does nothing is exactly the failure nobody files a ticket for.
      this.errorHandler.handleError(error);
      this.hasError.set(true);
      clearTimeout(this.errorResetTimer);
      this.errorResetTimer = setTimeout(
        () => this.hasError.set(false),
        ERROR_FEEDBACK_MS,
      );
    } finally {
      this.isSaving.set(false);
    }
  }
}
